/**
 * VoiceCall — duplex v3 (one smart MAIN + one fast fill channel). The host owns STT, TTS and MAIN; VoiceCall owns only
 * the fast model. STT finals go through TurnGate (hold / merge / dedupe / one goodbye) and reach MAIN via `sendTurn`
 * with the user's words verbatim. The fill channel (SecondChannel + Progress) speaks backchannels, fillers, bridges and
 * progress lines through `speak`/`show` only: it has NO path to MAIN — its options carry no turn callback, and nothing
 * it produces is ever passed to `sendTurn`.
 *
 * Language: per turn, detectLanguage on the user's own words (or the last language when the words fit it); the fill
 * channel speaks only in that language and stays silent when it is unknown (never English by default). MAIN answers
 * in the user's language from its own prompt.
 */
import { forComponent } from '../logging';
import type { ChatLike } from '../llm';
import { latestClaude } from '../models';
import { ChannelBanks, detectLanguage, fastBankGenerator, fitsLanguage, primaryLang, turnIntent, type Gender } from './channelLang';
import { OverlapJudge, overlapAside, overlapModelFor, type OverlapVerdict } from './overlap';
import { Progress, fastProgressGenerator, type ProgressOptions } from './progress';
import { channelDecide, fastLineGenerator, SecondChannel, type ChannelMode, type LineKind, type SecondChannelOptions } from './secondChannel';
import { TurnGate, type GateDrop, type GateTurn, type TurnGateOptions } from './turnGate';

const log = forComponent('VoiceCall');

export type FillKind = LineKind | 'progress';
/** MAIN's stream as the host forwards it. `id` = the turn (sendTurn's id); absent → the current turn. */
export interface MainEvent {
  type: 'start' | 'text' | 'tool' | 'tool_done' | 'audio' | 'done' | 'error';
  id?: string;
  text?: string;
  name?: string;
  args?: unknown;
}
/** One turn's timeline, ms from the user's last word (the STT's word clock when the host passes it, else the final's
 *  arrival). A stage that did not happen is absent. */
export interface TurnTimeline {
  /** The STT's final reached the call (STT latency + silence endpoint + the engine's merge hold). */
  final?: number;
  /** TurnGate sent the turn to MAIN (its own hold for a mid-phrase line included). */
  sent?: number;
  /** The fill channel chose its first line for this turn (bridge/filler/progress) — handed to the host, not yet heard. */
  fill?: number;
  fillKind?: string;
  /** That line's audio started (hostAudio: the host's player; else = fill). */
  fillAudio?: number;
  /** MAIN's first event (text or tool). */
  mainFirst?: number;
  /** MAIN's first text token. */
  mainText?: number;
  /** MAIN's first audio started (the host's player). */
  mainAudio?: number;
  /** false: a closer/acknowledgement ("Okay.", "תודה") — no answer due, no fill; its first sound is not a latency sample. */
  due?: false;
  /** false: the host gave no last-word time — the anchor is the final's arrival. */
  lastWord: boolean;
  /** How the turn ended: answered = MAIN audio played · cut = barged · replaced = a fuller re-final took it · silent = no MAIN audio. */
  end?: 'answered' | 'cut' | 'replaced' | 'silent';
}

export interface CallMetric {
  name: 'first_sound' | 'first_word' | 'turn' | 'drop' | 'hold' | 'fill' | 'lang' | 'timeline';
  id?: string;
  /** ms from the user's last word (see TurnTimeline) */
  ms?: number;
  by?: 'fill' | 'progress' | 'main';
  kind?: string;
  text?: string;
  lang?: string | null;
  /** name 'timeline': the turn's stages. */
  timeline?: TurnTimeline;
}

export class VoiceCallOptions {
  /** Host runs MAIN with exactly `text` (a replace: roll the `replaces` turn back first). */
  sendTurn: (text: string, o: { id: string; replaces?: string }) => void = () => {};
  /** Host aborts a running MAIN turn (barge-in). */
  abortTurn: (id: string) => void = () => {};
  /** Host TTS (fill lane): resolves when played; `false` = could not play. */
  speak: (text: string, kind: FillKind) => Promise<unknown> | boolean = () => false;
  /** Stop the in-flight fill line now (barge-in only). */
  cancel: () => void = () => {};
  /** Host caption — never history. */
  show: (text: string, kind: FillKind) => void = () => {};
  /** The fast model (lines, banks, progress) — used only with `ai`. */
  fastModel = latestClaude('haiku');
  /** Model client for the fast model + the channel decider / overlap judge. Absent → canned lines and rules only. */
  ai?: ChatLike;
  /** Cache dir for per-language banks (ChannelBanks). */
  bankCacheDir?: string;
  /** The host STT's language (BCP-47) — the starting language; each turn's own words still decide. */
  lang?: string;
  /** The voice's gender: generated fill/progress lines speak of themselves in it (Hebrew templates too). '' = neutral. */
  gender: Gender = '';
  aec = false;
  mode: ChannelMode = 'normal';
  gate: Partial<TurnGateOptions> = {};
  progress: Partial<ProgressOptions> = {};
  /** Extra SecondChannel options (tests, tuning). speak/show/cancel are always VoiceCall's own. */
  channel: Partial<SecondChannelOptions> = {};
  onMetric: (m: CallMetric) => void = () => {};
  /** The host reports when a fill line's audio REALLY starts (heard()). Off: a line handed to `speak` counts as heard —
   *  right only for a host whose speak() plays at once (a relay host would count lines its player later dropped). */
  hostAudio = false;
  now: () => number = () => performance.now();
  /** An aside (aside()) needs this long since the last one — a cue answered every time sounds scripted. */
  asideGapMs = 8000;
}

interface Turn { id: string; text: string; at: number; finalAt: number; lastWord: boolean; running: boolean; streamed: boolean; sounded: boolean; worded: boolean; done: boolean; marks: Partial<Record<'sent' | 'fill' | 'fillAudio' | 'mainFirst' | 'mainText' | 'mainAudio', number>>; fillKind?: string; end?: TurnTimeline['end']; closed: boolean; due: boolean }

export class VoiceCall {
  readonly fill: SecondChannel;
  readonly gate: TurnGate;
  readonly progress: Progress;
  private judge: OverlapJudge;
  private cur?: Turn;
  private lang: string | null = null;
  private lastAside = { text: '', at: -Infinity };
  private lastAudibleAt = -Infinity;
  private firstFinalAt: number | null = null; // the first final of the turn being formed (hold/merge)
  private lastWordAt: number | null = null; // the user's last word before the turn being formed (its latest final's)
  private earlyHeard: { at: number; by: CallMetric['by'] } | null = null; // a fill line heard while that turn was still held
  private progressTimer: ReturnType<typeof setTimeout> | null = null;
  private ended = false;

  public constructor(public options?: Partial<VoiceCallOptions>) {
    this.options = { ...new VoiceCallOptions(), ...options };
    const o = this.o;
    if (o.lang) this.lang = primaryLang(o.lang);
    const ai = o.ai;
    // The fill channel: speak/show/cancel only (no turn callback exists on it — no path to MAIN).
    this.fill = new SecondChannel({
      ...(ai ? {
        banks: new ChannelBanks({ generate: fastBankGenerator(ai, o.fastModel), ...(o.bankCacheDir ? { cacheDir: o.bankCacheDir } : {}) }),
        decide: channelDecide(ai),
        generate: fastLineGenerator(ai, o.fastModel, o.gender),
      } : {}),
      mode: o.mode,
      aec: o.aec,
      // v3 §3: first sound <1.2s p50 from the last word — the endpoint takes ~0.6s and the bridge's TTS ~0.2s, so the bridge
      // goes 300ms after the turn if MAIN is still silent (it always is that early: warm MAIN's first token is ≥1s), no veto
      // A short question gets that bridge too (by intent, not length); any other turn expecting an answer that got none
      // gets a late one at 1.3s from the last word while MAIN is still silent (API first byte runs 1-8s); a closer, none.
      bridgeDelayMs: 300, instantBridge: true, lateBridgeMs: 1300,
      ...(this.lang ? { lang: this.lang } : {}),
      ...o.channel,
      // the ledger: a turn's bridge moment that stayed silent, and why (its wait then has no first sound until MAIN speaks)
      onEvent: (e) => { if (e.trigger === 'working' && e.acted !== 'spoken') o.onMetric({ name: 'drop', ...(this.cur ? { id: this.cur.id } : {}), kind: `no bridge: ${e.reason ?? e.acted}`, ...(e.text ? { text: e.text } : {}) }); o.channel.onEvent?.(e); },
      speak: (text, kind) => this.say(text, kind),
      show: (text, kind) => o.show(text, kind),
      cancel: () => o.cancel(),
      now: o.now,
    });
    this.progress = new Progress({ ...(ai ? { generate: fastProgressGenerator(ai, o.fastModel, o.gender) } : {}), gender: o.gender, now: o.now, ...o.progress });
    this.judge = new OverlapJudge({ ...(ai ? { model: overlapModelFor(ai) } : {}), now: o.now });
    this.gate = new TurnGate({
      now: o.now,
      ...o.gate,
      onTurn: (t) => this.onTurn(t),
      onDrop: (text, why) => this.onDrop(text, why),
      onHold: (text) => o.onMetric({ name: 'hold', text }),
    });
  }
  private get o(): VoiceCallOptions { return this.options as VoiceCallOptions; }

  /** The language the fill channel speaks right now (null = unknown → silent). */
  get language(): string | null { return this.lang; }
  /** The MAIN turn in flight (or the last one). */
  get turn(): { id: string; text: string; running: boolean } | undefined { return this.cur && { id: this.cur.id, text: this.cur.text, running: this.cur.running }; }

  /** The host STT's language (BCP-47): the current language until the user's words say otherwise. */
  setLanguage(tag: string): void { const l = primaryLang(tag); if (!l) return; this.lang = l; this.fill.setLanguage(l, true); }

  /** STT partial (the whole partial). */
  interim(text: string, _atMs?: number, aec?: boolean): void {
    if (this.ended || !text.trim()) return;
    if (typeof aec === 'boolean') this.fill.options!.aec = aec;
    this.gate.speaking();
    this.follow(text);
    this.fill.onInterim(text);
  }

  /** STT final → TurnGate. `lastWordAgoMs`: how long ago the user's last word of it ended (the STT's word clock,
   *  bod-voice UtteranceMeta.lastWordAgoMs) — the turn's latency anchor; absent = the final's arrival. `lang`: the
   *  language the STT identified for it (UtteranceMeta.language) — what the turn's words follow when they alone can't tell
   *  (a short English line after a Hebrew one left the fill channel on Hebrew: its bridge was refused, the turn silent). */
  final(text: string, sttId?: string, o: { lastWordAgoMs?: number; lang?: string } = {}): void {
    if (this.ended) return;
    const now = this.o.now(), { lastWordAgoMs } = o;
    this.firstFinalAt ??= now;
    this.sttLang = o.lang ? primaryLang(o.lang) : null;
    this.lastWordAt = typeof lastWordAgoMs === 'number' && lastWordAgoMs >= 0 ? now - lastWordAgoMs : null;
    this.finalAt = now;
    this.gate.final(text, sttId);
  }
  private finalAt = 0;
  private sttLang: string | null = null; // the STT's language for the final being followed

  /** The host's player started a line's audio now (or in `inMs`): `fill`/`progress` = the fill channel's, `main` = MAIN's
   *  reply (as main({type:'audio'})). With hostAudio this is what first_sound counts. */
  heard(by: 'fill' | 'progress' | 'main', inMs = 0): void {
    if (this.ended) return;
    if (by === 'main') return this.main({ type: 'audio', ...(this.cur ? { id: this.cur.id } : {}) }, inMs);
    const at = this.o.now() + Math.max(0, inMs);
    if (this.gate.holding || !this.cur || this.cur.closed) { this.earlyHeard ??= { at, by }; return; } // the turn being formed: counted when it goes out
    this.lastAudibleAt = at;
    const c = this.cur;
    c.marks.fillAudio ??= at;
    this.sounded(c, by, at);
  }

  /** A v2 host that decides turns itself: the user's line already went to MAIN (no TurnGate, no sendTurn) — the fill
   *  channel follows its language and covers the wait. */
  hostTurn(text: string): void { if (this.ended || !text.trim()) return; this.follow(text); this.fill.onUserTurn(text); }

  pause(ms: number): void { if (!this.ended) this.fill.onPause(ms); }

  /** MAIN's stream. (`inMs`: an audio event's start is that far ahead — the host player's queue.) */
  main(ev: MainEvent, inMs = 0): void {
    const c = this.cur;
    if (!c || (ev.id && ev.id !== c.id)) return; // a replaced/aborted turn's late events
    switch (ev.type) {
      case 'start': c.running = true; return;
      case 'text':
        if (!ev.text) return;
        c.marks.mainFirst ??= this.o.now(); c.marks.mainText ??= this.o.now();
        if (!c.streamed) { c.streamed = true; this.gate.replyStarted(c.id); this.progress.text(); this.clearProgress(); }
        this.fill.onMainTokens(ev.text);
        return;
      case 'tool':
        c.marks.mainFirst ??= this.o.now();
        this.gate.replyStarted(c.id);
        void this.onTool(c, ev.name ?? '', ev.args);
        return;
      case 'tool_done': return;
      case 'audio': {
        const at = this.o.now() + Math.max(0, inMs);
        this.lastAudibleAt = at;
        this.fill.onAgentState('first_audio');
        this.sounded(c, 'main', at);
        if (!c.worded) { c.worded = true; c.marks.mainAudio = at; this.o.onMetric({ name: 'first_word', id: c.id, ms: Math.round(at - c.at), by: 'main' }); }
        if (c.done) this.close(c, 'answered');
        return;
      }
      case 'done': case 'error':
        c.running = false; c.done = true;
        this.clearProgress();
        this.fill.onAgentState('idle');
        if (c.worded) this.close(c, 'answered');
        return;
    }
  }

  /** The user barged in (hard): cut fill + MAIN audio (the host cuts its own), abort a running MAIN turn. */
  barge(): void {
    this.fill.onBargeIn();
    this.clearProgress();
    try { this.o.cancel(); } catch (e) { log.warn(`cancel threw: ${(e as Error).message}`); }
    if (this.cur?.running) { this.cur.running = false; this.o.abortTurn(this.cur.id); }
    if (this.cur && !this.cur.worded) this.cur.end ??= 'cut';
  }

  /** Soft barge-in: keep (a listener cue — no turn) or cut (→ barge()). */
  async overlap(text: string): Promise<OverlapVerdict> {
    const d = await this.judge.judge(text);
    if (d.verdict === 'cut') this.barge();
    return d.verdict;
  }

  /** The fill channel's spoken aside for an overlap judged 'keep' ("yeah" → "Oh yeah, definitely."): the host plays it
   *  while the reply is held, then resumes the same reply (bod-voice decideOverlap's aside). ≤4 canned words, never a
   *  claim, the cue's language; null = stay quiet (a plain listener cue, too soon after the last, or no line). It is a
   *  fill line: never MAIN history. */
  aside(text: string): string | null {
    const now = this.o.now();
    if (now - this.lastAside.at < this.o.asideGapMs) return null;
    const line = overlapAside(text, this.lang, this.lastAside.text);
    if (!line) return null;
    this.lastAside = { text: line, at: now };
    this.o.onMetric({ name: 'fill', kind: 'aside', text: line, lang: this.lang });
    return line;
  }

  /** Did the last barge cut OUR fill line `text`? (hosts strip it from the cut reply — it is not MAIN's speech) */
  spoke(text: string): boolean { return this.fill.spoke(text); }
  /** Resolves when the in-flight fill line finished — hosts queue MAIN audio behind it (stack, don't cut). */
  drain(): Promise<void> { return this.fill.drain(); }

  end(): void {
    if (this.cur) this.close(this.cur);
    this.ended = true;
    this.gate.end();
    this.clearProgress();
  }

  // ---- internals ----

  /** Follow the user's language from their own words: a confident detection, else the current language if the words
   *  fit it, else unknown (silent). Pinned into the channel so its own detection never diverges. */
  private follow(text: string): void {
    const fits = (l: string | null) => (l && fitsLanguage(text, l) ? l : null);
    const l = detectLanguage(text) ?? fits(this.sttLang) ?? fits(this.lang);
    if (l !== this.lang) this.o.onMetric({ name: 'lang', lang: l, text });
    this.lang = l;
    if (l) this.fill.setLanguage(l, true);
  }

  private onTurn(t: GateTurn): void {
    const now = this.o.now(), lastWord = this.lastWordAt !== null;
    const at = this.lastWordAt ?? this.firstFinalAt ?? now; // the user's last word (else the final's arrival)
    const finalAt = this.finalAt || now;
    this.firstFinalAt = null; this.lastWordAt = null;
    if (this.cur) this.close(this.cur, t.replaces === this.cur.id ? 'replaced' : undefined);
    this.follow(t.text);
    this.clearProgress();
    this.cur = { id: t.id, text: t.text, at, finalAt, lastWord, running: true, streamed: false, sounded: false, worded: false, done: false, marks: { sent: now }, closed: false, due: turnIntent(t.text) !== 'closer' };
    const early = this.earlyHeard; this.earlyHeard = null;
    if (early && early.at >= at) { this.cur.marks.fillAudio = early.at; this.sounded(this.cur, early.by, early.at); }
    this.progress.turn();
    this.o.onMetric({ name: 'turn', id: t.id, kind: t.kind, text: t.text });
    this.o.sendTurn(t.text, { id: t.id, ...(t.replaces ? { replaces: t.replaces } : {}) });
    if (t.kind === 'new') this.fill.onUserTurn(t.text, now - at); // a replace/extend keeps the bridge already armed (no second one)
  }

  private onDrop(text: string, why: GateDrop): void {
    if (!this.gate.holding) { this.firstFinalAt = null; this.lastWordAt = null; this.earlyHeard = null; }
    this.o.onMetric({ name: 'drop', kind: why, text });
  }

  /** Every fill/progress line passes here: only in the turn's known language, never after the call ended. */
  private say(text: string, kind: FillKind): Promise<unknown> | boolean {
    if (this.ended || !this.lang || this.fill.language !== this.lang) return false;
    let r: Promise<unknown> | boolean;
    try { r = this.o.speak(text, kind); } catch (e) { log.warn(`speak threw: ${(e as Error).message}`); return false; }
    if (r === false) return false;
    this.o.onMetric({ name: 'fill', kind, text, lang: this.lang });
    const c = this.cur && !this.cur.closed ? this.cur : undefined;
    const forTurn = !!c && (kind === 'bridge' || kind === 'progress' || (kind === 'filler' && c.running && !c.sounded));
    if (c && forTurn && c.marks.fill === undefined) { c.marks.fill = this.o.now(); c.fillKind = kind; }
    if (this.o.hostAudio) return r; // heard() says when it really plays (a dropped line never counts)
    this.lastAudibleAt = this.o.now();
    if (c && forTurn) { c.marks.fillAudio ??= this.o.now(); this.sounded(c, kind === 'progress' ? 'progress' : 'fill'); }
    return r;
  }

  private sounded(c: Turn, by: CallMetric['by'], at = this.o.now()): void {
    if (c.sounded) return;
    c.sounded = true;
    this.o.onMetric(c.due ? { name: 'first_sound', id: c.id, ms: Math.round(at - c.at), by } : { name: 'first_sound', id: c.id, by, kind: 'no answer due' }); // a closer is no latency sample
  }

  /** The turn is over (answered + audible, replaced, the next turn, the call's end): its timeline, once. A turn whose MAIN
   *  never became audible still reports first_word — with no ms and why (cut / replaced / silent) — so every turn has one. */
  private close(c: Turn, end?: TurnTimeline['end']): void {
    if (c.closed) return;
    c.closed = true;
    c.end = c.worded ? 'answered' : end ?? c.end ?? 'silent';
    if (!c.worded) this.o.onMetric({ name: 'first_word', id: c.id, by: 'main', kind: c.end });
    const rel = (t?: number) => (t === undefined ? undefined : Math.round(t - c.at));
    const m = c.marks, tl: TurnTimeline = { final: rel(c.finalAt), sent: rel(m.sent), fill: rel(m.fill), ...(c.fillKind ? { fillKind: c.fillKind } : {}),
      fillAudio: rel(m.fillAudio), mainFirst: rel(m.mainFirst), mainText: rel(m.mainText), mainAudio: rel(m.mainAudio), lastWord: c.lastWord, end: c.end, ...(c.due ? {} : { due: false as const }) };
    for (const k of Object.keys(tl) as (keyof TurnTimeline)[]) if (tl[k] === undefined) delete tl[k];
    this.o.onMetric({ name: 'timeline', id: c.id, text: c.text, timeline: tl });
  }

  private async onTool(c: Turn, name: string, args: unknown): Promise<void> {
    const lang = this.lang;
    if (!lang || c.streamed) return;
    const line = await this.progress.line(name, args, lang);
    if (!line || this.cur !== c || c.streamed || !c.running || this.lang !== lang) return;
    this.clearProgress();
    const tryNow = () => {
      this.progressTimer = null;
      if (this.ended || this.cur !== c || c.streamed || !c.running) return;
      if (!this.progress.due(this.lastAudibleAt)) { this.progressTimer = setTimeout(tryNow, 250); return; }
      if (this.say(line, 'progress') !== false) { this.progress.spoke(line); this.o.show(line, 'progress'); }
    };
    tryNow();
  }

  private clearProgress(): void { if (this.progressTimer) { clearTimeout(this.progressTimer); this.progressTimer = null; } }
}
